import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Density = 'compact' | 'comfortable';

const STORAGE_KEY = 'isoc.density';

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
  toggle: () => void;
}

const DensityContext = createContext<DensityContextValue | null>(null);

function readInitial(): Density {
  if (typeof window === 'undefined') return 'comfortable';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'compact' ? 'compact' : 'comfortable';
}

function apply(density: Density) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.density = density;
}

export function DensityProvider({ children }: { children: React.ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => readInitial());

  useEffect(() => {
    apply(density);
  }, [density]);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    try {
      window.localStorage.setItem(STORAGE_KEY, d);
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(
    () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
    [density, setDensity]
  );

  const value = useMemo(
    () => ({ density, setDensity, toggle }),
    [density, setDensity, toggle]
  );

  return (
    <DensityContext.Provider value={value}>{children}</DensityContext.Provider>
  );
}

export function useDensity() {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error('useDensity must be used inside <DensityProvider>');
  return ctx;
}
